import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Clock,
    Calendar,
    Gift,
    Users,
    Briefcase,
    Bell,
    CheckCircle,
    LogOut,
    Pause,
    Play,
    Eye,
    Clock4,
    Download,
    Search,
    UserCheck,
    AlertCircle,
    X,
    XCircle,
    Building,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { HOME, CLOCKIFY, NEW_EMPLOYEE_ATTENDANCE_DAILY, NEW_EMPLOYEE_ATTENDANCE_CORRECTION_REQUEST } from "../../constants/api";
import { formatDate, isToday } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { useNavigate } from "react-router-dom";
import { Input } from "../../components/ui/input";

function TimesheetManagement() {
    const { fetch, update } = useApiMethods();
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("late");
    const [searchText, setSearchText] = useState("");
    const [lateEmployees, setLateEmployees] = useState([]);
    const [attendanceRequests, setAttendanceRequests] = useState([]);
    const [shortDutyEmployees, setShortDutyEmployees] = useState([]);
    const [newRequestAvailable, setNewRequestAvailable] = useState(false);
    const [loading, setLoading] = useState(false);

    const is_super_admin = JSON.parse(sessionStorage.getItem("is_super_admin"));
    const is_admin = JSON.parse(sessionStorage.getItem("is_admin"));

    // Fetch data functions (same as before)
    const fetchLateEmployees = async () => {
        try {
            let date = new Date().toISOString().split('T')[0];
            const query = `date=${date}&late_only=true&search=${searchText}`;
            const response = await fetch(`${NEW_EMPLOYEE_ATTENDANCE_DAILY}?${query}`);
            setLateEmployees(response.data || []);
        } catch (error) {
            console.error("Error fetching late employees:", error);
        }
    };

    const fetchAttendanceRequests = async () => {
        try {
            const response = await fetch(NEW_EMPLOYEE_ATTENDANCE_CORRECTION_REQUEST);
            const data = response.data || [];
            setAttendanceRequests(data);
            setNewRequestAvailable(data.some(item => item.status === "pending"));
        } catch (error) {
            console.error("Error fetching attendance requests:", error);
        }
    };

    const fetchShortDutyEmployees = async () => {
        try {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const date = yesterday.toISOString().split('T')[0];
            const query = `date=${date}&is_short_duty=true&search=${searchText}`;
            const response = await fetch(`${NEW_EMPLOYEE_ATTENDANCE_DAILY}?${query}`);
            setShortDutyEmployees(response.data || []);
        } catch (error) {
            console.error("Error fetching short duty employees:", error);
        }
    };

    useEffect(() => {
        if (open && (is_admin || is_super_admin)) {
            switch (activeTab) {
                case "late":
                    fetchLateEmployees();
                    break;
                case "requests":
                    fetchAttendanceRequests();
                    break;
                case "short-duty":
                    fetchShortDutyEmployees();
                    break;
            }
        }
    }, [open, activeTab, searchText]);

    useEffect(() => {
        if (!is_admin && !is_super_admin) return

        fetchAttendanceRequests();
    }, [])

    const handleDownload = () => {
        let header = "";
        let csvRows = [];

        if (activeTab === "late") {
            header = "Name,Department,Clock In Status\n";
            csvRows = lateEmployees.map(emp => {
                const employee = emp.employee_details || {};
                const name = `${employee.title || ""} ${employee.employee_name || ""}`.trim();
                const department = employee.department?.name || "N/A";
                const status = emp.check_in ? `Late at ${new Date(emp.check_in).toLocaleTimeString()}` : "Not Clocked In";
                return `${name},${department},${status}`;
            });
        } else if (activeTab === "requests") {
            header = "Employee ID,Employee Name,Date,Check In,Check Out,Status,Reason,Requested On,Admin Comment\n";
            csvRows = attendanceRequests.map(req => {
                return [
                    req.employee_id,
                    req.employee_name,
                    req.date,
                    req.check_in,
                    req.check_out,
                    req.status,
                    `"${req.reason}"`,
                    req.requested_on,
                    `"${req.admin_comment || ""}"`
                ].join(",");
            });
        } else if (activeTab === "short-duty") {
            header = "Name,Department\n";
            csvRows = shortDutyEmployees.map(emp => {
                const employee = emp.employee_details || {};
                const name = `${employee.title || ""} ${employee.employee_name || ""}`.trim();
                const department = employee.department?.name || "N/A";
                return `${name},${department}`;
            });
        }

        const blob = new Blob([header + csvRows.join("\n")], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${activeTab}-summary.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const handleRequestAction = async (request, action, comment = "") => {
        try {
            const response = await update(NEW_EMPLOYEE_ATTENDANCE_CORRECTION_REQUEST, {
                id: request.id,
                check_in: request.req_check_in,
                check_out: request.req_check_out,
                status: action,
                admin_comment: comment || `${action} by admin`,
            });

            if (response.status !== 'fail') {
                ToastMsg("success", `Request ${action} successfully`);
                fetchAttendanceRequests()
            } else {
                ToastMsg("error", `Request ${action} failed: ${response.message || 'Unknown error'}`);
            }

        } catch (error) {
            console.error("Update request failed:", error);
            const errorMessage = error.message || "Failed to update request";
            ToastMsg("error", errorMessage);
        }
    };

    // Manual UI Components with dark mode support
    const Card = ({ children, className = "" }) => (
        <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${className}`}>
            {children}
        </div>
    );

    const CardContent = ({ children, className = "" }) => (
        <div className={`p-4 ${className}`}>
            {children}
        </div>
    );

    const Avatar = ({ src, fallback, className = "" }) => (
        <div className={`w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center overflow-hidden ${className}`}>
            {src ? (
                <img src={src} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {fallback}
                </span>
            )}
        </div>
    );

    const Badge = ({ children, variant = "default", className = "" }) => {
        const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors";

        const variants = {
            default: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700",
            success: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800",
            destructive: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800",
            warning: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
            secondary: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800"
        };

        return (
            <span className={`${baseClasses} ${variants[variant]} ${className}`}>
                {children}
            </span>
        );
    };

    const Input = ({ placeholder, value, onChange, className = "" }) => (
        <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors ${className}`}
        />
    );

    // Tab Components with dark mode
    const TabButton = ({ value, label, count, hasNew }) => (
        <button
            onClick={() => setActiveTab(value)}
            className={`flex items-center mb-2 gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === value
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
        >
            {label}
            {count > 0 && (
                <span className={`px-2 py-1 text-xs rounded-full transition-colors ${activeTab === value
                    ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                    {count}
                </span>
            )}
            {hasNew && (
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            )}
        </button>
    );

    const TabContent = ({ value, children }) => (
        <div className={`${activeTab === value ? 'block' : 'hidden'} flex-1 overflow-auto p-6`}>
            {children}
        </div>
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'rejected': return 'destructive';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    if (!is_admin && !is_super_admin) return null;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
                <Clock className="w-4 h-4" />
                Timesheet Management
                {newRequestAvailable && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                )}
            </button>

            {open && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex justify-end">
                    <div className="bg-white dark:bg-gray-900 w-full max-w-4xl h-full overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Timesheet Management</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Manage employee attendance and requests</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleDownload}
                                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
                                >
                                    <Download className="w-4 h-4" />
                                    Export
                                </button>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        {/* Manual Tabs */}
                        <div className="flex-1 overflow-hidden flex flex-col">
                            {/* Tab Headers */}
                            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex gap-2">
                                    <TabButton
                                        value="late"
                                        label="Late Summary"
                                        count={lateEmployees.length}
                                    />
                                    <TabButton
                                        value="requests"
                                        label="Requests"
                                        count={attendanceRequests.length}
                                        hasNew={newRequestAvailable}
                                    />
                                    <TabButton
                                        value="short-duty"
                                        label="Short Duty"
                                        count={shortDutyEmployees.length}
                                    />
                                </div>
                            </div>

                            {/* Search */}
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                                    <Input
                                        placeholder={`Search ${activeTab === 'late' ? 'late employees' : activeTab === 'requests' ? 'requests' : 'short duty employees'}...`}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                            </div>

                            {/* Tab Contents */}
                            <TabContent value="late">
                                <div className="space-y-4">
                                    {lateEmployees.map((employee) => (
                                        <Card key={employee.id} className="hover:shadow-lg transition-all duration-200">
                                            <CardContent>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <Avatar
                                                            src={employee.employee_details?.profile}
                                                            fallback={employee.employee_details?.employee_name?.charAt(0)}
                                                        />
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">
                                                                {employee.employee_details?.employee_name}
                                                            </p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                {employee.employee_details?.department?.name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <Badge variant={employee.check_in ? "warning" : "destructive"}>
                                                            {employee.check_in ? "Late" : "Not Clocked In"}
                                                        </Badge>
                                                        {employee.check_in && (
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                                {new Date(employee.check_in).toLocaleTimeString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {lateEmployees.length === 0 && (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                            <p>No late employees today</p>
                                        </div>
                                    )}
                                </div>
                            </TabContent>

                            <TabContent value="requests">
                                <div className="space-y-4">
                                    {attendanceRequests.map((request) => (
                                        <Card key={request.id} className="hover:shadow-lg transition-all duration-200">
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between gap-4">
                                                    {/* Left Section - Employee Details */}
                                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                                        <EmployeeDetails
                                                            details={{
                                                                title: request.employee_details?.title || "",
                                                                first_name: request.employee_name || "",
                                                                last_name: "",
                                                                designation_id: {
                                                                    designation_name: request.employee_details?.designation?.name || ""
                                                                },
                                                                id: request.employee_id,
                                                                profile: request.employee_details?.profile
                                                            }}
                                                            size="sm"
                                                        />
                                                    </div>

                                                    {/* Right Section - Request Details & Actions */}
                                                    <div className="flex flex-col items-end gap-3 flex-shrink-0">
                                                        {/* Status Badge */}
                                                        <Badge variant={getStatusColor(request.status)} className="text-xs font-medium">
                                                            {request.status.toUpperCase()}
                                                        </Badge>

                                                        {/* Action Buttons for Pending Requests */}
                                                        {request.status === 'pending' && (
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => handleRequestAction(request, 'approved')}
                                                                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-md transition-colors font-medium"
                                                                >
                                                                    <CheckCircle className="w-3 h-3" />
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRequestAction(request, 'rejected')}
                                                                    className="flex items-center gap-1 px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-xs rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors font-medium"
                                                                >
                                                                    <XCircle className="w-3 h-3" />
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Request Details */}
                                                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                        {/* Date & Time Information */}
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span>
                                                                <span className="text-gray-900 dark:text-white">{request.date}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Time:</span>
                                                                <span className="text-gray-900 dark:text-white">{request.check_in} - {request.check_out}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Requested:</span>
                                                                <span className="text-gray-900 dark:text-white text-xs">{request.requested_on}</span>
                                                            </div>
                                                        </div>

                                                        {/* Reason & Admin Comment */}
                                                        <div className="space-y-2">
                                                            <div>
                                                                <span className="font-medium text-gray-700 dark:text-gray-300 block mb-1">Reason:</span>
                                                                <p className="text-gray-900 dark:text-white text-sm bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                                                                    {request.reason}
                                                                </p>
                                                            </div>
                                                            {request.admin_comment && (
                                                                <div>
                                                                    <span className="font-medium text-gray-700 dark:text-gray-300 block mb-1">Admin Note:</span>
                                                                    <p className={`text-sm p-2 rounded border ${request.status === 'approved'
                                                                        ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800'
                                                                        : request.status === 'rejected'
                                                                            ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800'
                                                                            : 'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                                                                        }`}>
                                                                        {request.admin_comment}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Additional Metadata */}
                                                <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-4">
                                                        <span className="flex items-center gap-1">
                                                            <UserCheck className="w-3 h-3" />
                                                            ID: {request.employee_id}
                                                        </span>
                                                        {request.employee_details?.department?.name && (
                                                            <span className="flex items-center gap-1">
                                                                <Building className="w-3 h-3" />
                                                                {request.employee_details.department.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-gray-400 dark:text-gray-500">
                                                        Request #{request.id}
                                                    </span>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {attendanceRequests.length === 0 && (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                            <p>No attendance requests</p>
                                        </div>
                                    )}
                                </div>
                            </TabContent>

                            <TabContent value="short-duty">
                                <div className="space-y-4">
                                    {shortDutyEmployees.map((employee) => (
                                        <Card key={employee.id} className="hover:shadow-lg transition-all duration-200">
                                            <CardContent>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <Avatar
                                                            src={employee.employee_details?.profile}
                                                            fallback={employee.employee_details?.employee_name?.charAt(0)}
                                                        />
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">
                                                                {employee.employee_details?.employee_name}
                                                            </p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                {employee.employee_details?.department?.name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Badge variant="default">Short Duty</Badge>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {shortDutyEmployees.length === 0 && (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                            <p>No short duty records</p>
                                        </div>
                                    )}
                                </div>
                            </TabContent>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function AttendanceCard({ attendanceData, attendanceLoading, refreshData }) {
    const { fetch } = useApiMethods();
    const [loading, setLoading] = useState(false);

    // Local attendance state mirrors minimal bits we need to control UI
    const [attendance, setAttendance] = useState({
        isPresent: false,
        clockIn: null,
        clockOut: null,
        paused: false,
        clockStatus: null,
    });

    // elapsedMs is always the canonical source of time for display (ms)
    const [elapsedMs, setElapsedMs] = useState(0);

    // pausedElapsedMs stores the elapsed time when paused (for "Worked" display)
    const [pausedElapsedMs, setPausedElapsedMs] = useState(0);

    // derive stable values from attendanceData
    const checkIn = attendanceData?.check_in_time ?? null;
    const checkOut = attendanceData?.check_out_time ?? null;
    const statusFromAPI = attendanceData?.clock_status ?? null;
    const workingSecs = attendanceData?.working_hrs ? parseFloat(attendanceData.working_hrs) : null; // seconds
    const pauseTime = attendanceData?.pause;
    const resumeTime = attendanceData?.resume;

    // baseStartMs is the timestamp (ms) of check-in if present
    const baseStartMs = useMemo(() => {
        if (!checkIn) return null;
        const t = new Date(checkIn).getTime();
        return Number.isFinite(t) ? t : null;
    }, [checkIn]);

    // Format helpers
    const formatDiff = (ms) => {
        if (typeof ms !== "number" || ms <= 0) return "0h 0m 0s";
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${hours}h ${minutes}m ${seconds}s`;
    };

    const formatTimeDisplay = (ms) => {
        if (typeof ms !== "number" || ms <= 0) return "00:00:00";
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };

    const formatTime = (dateString) => {
        if (!dateString) return "--";
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return "--";
        return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    // Calculate current status based on API data (more reliable)
    const currentStatus = useMemo(() => {
        if (!checkIn) return "not_started";
        if (checkOut) return "completed";
        if (statusFromAPI === "pause") return "paused";
        return "active";
    }, [checkIn, checkOut, statusFromAPI]);

    // Initialize attendance local state when API data changes
    useEffect(() => {
        const isPresent = !!(checkIn && !checkOut);
        const isPaused = statusFromAPI === "pause";

        setAttendance((prev) => ({
            ...prev,
            isPresent,
            clockIn: checkIn,
            clockOut: checkOut,
            paused: isPaused,
            clockStatus: statusFromAPI,
        }));

        // Initialize elapsed time based on API data
        if (checkOut) {
            // Session completed - use final worked time
            const workedMs = workingSecs ? Math.floor(workingSecs * 1000) : 0;
            setElapsedMs(workedMs);
            setPausedElapsedMs(workedMs);
        } else if (isPaused) {
            // Currently paused - use worked time from API
            const workedMs = workingSecs ? Math.floor(workingSecs * 1000) : 0;
            setElapsedMs(workedMs);
            setPausedElapsedMs(workedMs);
        } else if (checkIn && resumeTime) {
            // Resumed session - calculate from pause time + time since resume
            const baseWorkedMs = workingSecs ? Math.floor(workingSecs * 1000) : 0;
            const timeSinceResume = Date.now() - new Date(resumeTime).getTime();
            setElapsedMs(baseWorkedMs + timeSinceResume);
        } else if (checkIn) {
            // Active session (no resume) - calculate from clock-in
            const initialElapsed = Math.max(0, Date.now() - baseStartMs);
            setElapsedMs(initialElapsed);
        } else {
            // No session
            setElapsedMs(0);
            setPausedElapsedMs(0);
        }
    }, [checkIn, checkOut, statusFromAPI, workingSecs, baseStartMs, resumeTime]);

    // Calculate shift duration
    const SHIFT_DURATION_MS = useMemo(() => {
        if (!attendanceData?.shift_start_time || !attendanceData?.shift_end_time)
            return 9 * 60 * 60 * 1000; // fallback to 9 hours

        const start = new Date(attendanceData.shift_start_time).getTime();
        const end = new Date(attendanceData.shift_end_time).getTime();
        return end - start;
    }, [attendanceData?.shift_start_time, attendanceData?.shift_end_time]);

    // Calculate display values
    const displayWorkedMs = useMemo(() => {
        if (currentStatus === "paused") {
            return pausedElapsedMs;
        }
        if (currentStatus === "completed" && workingSecs) {
            return Math.floor(workingSecs * 1000);
        }
        return elapsedMs;
    }, [currentStatus, pausedElapsedMs, workingSecs, elapsedMs]);

    const remainingMs = Math.max(0, SHIFT_DURATION_MS - displayWorkedMs);

    // Timer effect - ONLY run when actively working
    useEffect(() => {
        if (currentStatus !== "active" || !checkIn || checkOut) {
            return undefined;
        }

        const tick = () => {
            if (resumeTime) {
                // Resumed session: base time from working_hrs + time since resume
                const baseWorkedMs = workingSecs ? Math.floor(workingSecs) : 0;
                const timeSinceResume = Date.now() - new Date(resumeTime).getTime();
                setElapsedMs(baseWorkedMs + timeSinceResume);
            } else {
                // Fresh session: calculate from clock-in
                setElapsedMs(Date.now() - baseStartMs);
            }
        };

        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [currentStatus, checkIn, checkOut, workingSecs, resumeTime, baseStartMs]);

    // Effect to sync pausedElapsedMs when pausing
    useEffect(() => {
        if (currentStatus === "paused" && elapsedMs > 0) {
            setPausedElapsedMs(elapsedMs);
        }
    }, [currentStatus, elapsedMs]);

    // Helper: unified API call for clock actions
    const handleClockify = async (action) => {
        if (loading) return false;
        setLoading(true);

        try {
            const payload = {
                status: action,
                attendanceId: attendanceData?.attendance_id,
            };

            const response = await fetch(CLOCKIFY, payload);

            if (response && response.status === "success") {
                ToastMsg("success", response.message || `${action.replace("_", " ")} successful`);
                // refresh data to get canonical backend state
                if (refreshData) await refreshData();
                return true;
            } else {
                ToastMsg("error", response?.message || `Failed to ${action}`);
                return false;
            }
        } catch (err) {
            console.error("Clockify error:", err);
            ToastMsg("error", err?.message || "Something went wrong");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Actions (pause/resume/clock_in/clock_out) update local optimistic state then call API
    const handlePause = async () => {
        // Capture current elapsed time BEFORE optimistic update
        const currentElapsed = elapsedMs;

        // optimistic UI update: set paused true
        setAttendance((p) => ({ ...p, paused: true, clockStatus: "pause" }));
        // Freeze the display immediately
        setPausedElapsedMs(currentElapsed);

        await handleClockify("pause");
    };

    const handleResume = async () => {
        // optimistic update: unpause
        setAttendance((p) => ({ ...p, paused: false, clockStatus: "resume" }));

        await handleClockify("resume");
    };

    const handleClockIn = async () => {
        const nowIso = new Date().toISOString();
        setAttendance((p) => ({ ...p, isPresent: true, paused: false, clockIn: nowIso, clockStatus: "resume", clockOut: null }));
        setElapsedMs(0);
        setPausedElapsedMs(0);
        await handleClockify("clock_in");
    };

    const handleClockOut = async () => {
        const success = await handleClockify("clock_out");
        if (success) {
            setAttendance((p) => ({ ...p, isPresent: false, clockOut: new Date().toISOString(), clockStatus: "completed" }));
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 min-h-48 p-5 rounded-2xl shadow-md border hover:shadow-lg transition">
            {attendanceLoading ? (
                <span className="min-h-[150px] flex items-center justify-center">
                    <div className="dark:bg-gray-900 rounded-md">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                    </div>
                </span>
            ) : (
                <>
                    <h2 className="text-xs font-semibold mb-2 -mt-2 uppercase text-gray-700 dark:text-gray-200 flex justify-between items-center">
                        <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            Attendance
                        </span>
                    </h2>

                    <div className="flex justify-between items-center">
                        <div>
                            {currentStatus === "completed" ? (
                                <>
                                    <p className="text-sm text-gray-500">Total Worked</p>
                                    <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">
                                        {formatDiff(displayWorkedMs)}
                                    </h3>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-500">Time left</p>
                                    <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">
                                        {formatDiff(remainingMs)}
                                    </h3>
                                </>
                            )}
                        </div>

                        <div className="mb-3">
                            <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${currentStatus === "active"
                                    ? "bg-green-100 text-green-800"
                                    : currentStatus === "paused"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : currentStatus === "completed"
                                            ? "bg-blue-100 text-blue-800"
                                            : "bg-gray-100 text-gray-800"
                                    }`}
                            >
                                {currentStatus === "active" && "🟢 Active"}
                                {currentStatus === "paused" && "🟡 Paused"}
                                {currentStatus === "completed" && "🔵 Completed"}
                                {currentStatus === "not_started" && "⚪ Not Started"}
                            </span>
                        </div>
                    </div>

                    <div className="mt-2 flex flex-wrap justify-between text-xs text-gray-600 dark:text-gray-300">
                        <div className="pr-3">
                            <span className="font-medium">Clock In:</span> {formatTime(checkIn)}
                        </div>
                        <div>
                            <span className="font-medium">Clock Out:</span> {formatTime(checkOut)}
                        </div>
                    </div>

                    {/* Live Timer Display for Active Session */}
                    {currentStatus === "active" && (
                        <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <p className="text-xs text-green-700 dark:text-green-300 font-medium">Live: {formatTimeDisplay(elapsedMs)}</p>
                        </div>
                    )}

                    {/* Paused State Display - show FROZEN worked time using pausedElapsedMs */}
                    {currentStatus === "paused" && (
                        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                            <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">⏸️ Worked: {formatDiff(pausedElapsedMs)}</p>
                        </div>
                    )}

                    {/* Completed Session Summary */}
                    {currentStatus === "completed" && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-blue-600" />
                                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                        Shift Completed
                                    </span>
                                </div>
                                <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                                    {formatDiff(displayWorkedMs)}
                                </div>
                            </div>
                            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                                Great work today! 🎉
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex justify-end gap-2">
                        {currentStatus === "not_started" ? (
                            <button
                                onClick={handleClockIn}
                                disabled={loading}
                                className="flex items-center gap-1 text-xs px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <CheckCircle className="w-3 h-3" />
                                {loading ? "Processing..." : "Clock In"}
                            </button>
                        ) : currentStatus === "active" ? (
                            <>
                                <button
                                    onClick={handlePause}
                                    disabled={loading}
                                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Pause className="w-3 h-3" />
                                    {loading ? "..." : "Pause"}
                                </button>
                                <button
                                    onClick={handleClockOut}
                                    disabled={loading}
                                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <LogOut className="w-3 h-3" />
                                    {loading ? "..." : "Clock Out"}
                                </button>
                            </>
                        ) : currentStatus === "paused" ? (
                            <>
                                <button
                                    onClick={handleResume}
                                    disabled={loading}
                                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Play className="w-3 h-3" />
                                    {loading ? "..." : "Resume"}
                                </button>
                                <button
                                    onClick={handleClockOut}
                                    disabled={loading}
                                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <LogOut className="w-3 h-3" />
                                    {loading ? "..." : "Clock Out"}
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center gap-2 w-full justify-between">
                                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                    <Calendar className="w-3 h-3" />
                                    <span>Session ended</span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

const Home = () => {
    const today = Date.now();
    const user_name = sessionStorage.getItem("employee_name") || "User";

    // states
    // const [loading, setLoading] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [birthdays, setBirthdays] = useState([]);
    const [anniversaries, setAnniversaries] = useState([]);
    const [onLeave, setOnLeave] = useState([]);
    const [attendanceData, setAttendanceData] = useState(() => {
        const stored = sessionStorage.getItem("shiftDetails");
        return stored ? JSON.parse(stored) : null;
    });
    const is_password_updated = JSON.parse(sessionStorage.getItem("is_password_updated"));
    let navigate = useNavigate();
    const [attendanceLoading, setAttendanceLoading] = useState(true);
    const { fetch } = useApiMethods();

    const formatNoticeDate = (dateString, timeString) => {
        if (!dateString) return "N/A";

        const date = new Date(`${dateString}T${timeString || "00:00:00"}`);
        const options = { day: "numeric", month: "long", year: "numeric" };
        return date.toLocaleDateString("en-US", options); // Example: "13 September 2025"
    };

    const calculateYears = (dateString) => {
        const startDate = new Date(dateString);   // e.g. 2020-10-01
        const today = new Date();

        let years = today.getFullYear() - startDate.getFullYear();

        // If the anniversary hasn’t occurred yet this year, subtract 1
        const hasHadAnniversary =
            today.getMonth() > startDate.getMonth() ||
            (today.getMonth() === startDate.getMonth() && today.getDate() >= startDate.getDate());

        if (!hasHadAnniversary) {
            years -= 1;
        }

        return years;
    };

    const getData = useCallback(async () => {
        try {
            const response = await fetch(HOME);

            const noticeBoardData = response?.data?.notice_board;
            const upcomingEvents = response?.data?.upcoming_events;
            const custom_configuration = response?.data?.payroll_custom_configuration;
            const password_policy = response?.data?.password_policy;
            console.log(response.data);

            // {
            //     "notice_board": [
            //         {
            //             "id": 1,
            //             "created_by": null,
            //             "created_date": "2024-10-19T08:52:04.019130",
            //             "modified_by": null,
            //             "modified_date": "2024-10-19T08:52:04.019160",
            //             "is_active": true,
            //             "is_draft": false,
            //             "notes": null,
            //             "code": null,
            //             "company_id": 2,
            //             "notice_title": "Diwali Celebration",
            //             "description": "Events conducted on 31-10-2024",
            //             "published_date": "2024-10-19",
            //             "published_time": "08:52:03",
            //             "status": "Published",
            //             "attachment": null,
            //             "notice_to_departments": [
            //                 1,
            //                 2
            //             ],
            //             "expiry_date": "2026-09-29",
            //             "expiry_time": "09:59:00",
            //             "notice_by": "Admin Admin",
            //             "priority_level": "High",
            //             "notice_to_employee": [
            //                 {
            //                     "id": 2,
            //                     "title": "Mr",
            //                     "employee_id": "EMED001",
            //                     "employee_name": "Jana Admin",
            //                     "profile": "",
            //                     "designation": {
            //                         "id": 1,
            //                         "name": "Software Engineer"
            //                     },
            //                     "department": {
            //                         "id": 2,
            //                         "name": "Development"
            //                     },
            //                     "employee_status": true
            //                 }
            //             ]
            //         }
            //     ],
            //     "upcoming_events": [
            //         {
            //             "employee_name": "Patricia Young",
            //             "employee_id": 49,
            //             "designation": {
            //                 "designation_id": 1,
            //                 "designation_name": "Software Engineer"
            //             },
            //             "date": "2007-11-29",
            //             "event": "anniversary"
            //         },
            //         {
            //             "employee_name": "John Chapman",
            //             "employee_id": 4,
            //             "designation": {
            //                 "designation_id": 1,
            //                 "designation_name": "Software Engineer"
            //             },
            //             "date": "2018-12-03",
            //             "event": "anniversary"
            //         },
            //         {
            //             "employee_name": "Cathy Wallace",
            //             "employee_id": 15,
            //             "designation": {
            //                 "designation_id": 1,
            //                 "designation_name": "Software Engineer"
            //             },
            //             "date": "2010-12-08",
            //             "event": "anniversary"
            //         },
            //         {
            //             "employee_name": "Bobby Rasmussen",
            //             "employee_id": 41,
            //             "designation": {
            //                 "designation_id": 1,
            //                 "designation_name": "Software Engineer"
            //             },
            //             "date": "2014-12-21",
            //             "event": "anniversary"
            //         },
            //         {
            //             "employee_name": "Kathleen Ball",
            //             "employee_id": 24,
            //             "designation": {
            //                 "designation_id": 1,
            //                 "designation_name": "Software Engineer"
            //             },
            //             "date": "2014-12-26",
            //             "event": "anniversary"
            //         }
            //     ],
            //     "my_leaves": {
            //         "total_leaves": 24,
            //         "available_leaves": 21.5,
            //         "leaves_taken": 2.5
            //     },
            //     "password_policy": [
            //         {
            //             "id": 2,
            //             "created_by": null,
            //             "created_date": "2024-10-19T07:10:32.746418",
            //             "modified_by": null,
            //             "modified_date": "2024-10-19T07:10:32.746438",
            //             "is_active": true,
            //             "is_draft": false,
            //             "notes": null,
            //             "code": null,
            //             "company_id": 2,
            //             "min_length": 1,
            //             "max_length": 15,
            //             "min_number": 1,
            //             "max_number": 5,
            //             "min_lower": 1,
            //             "max_lower": 5,
            //             "min_upper": 1,
            //             "max_upper": 5,
            //             "min_chars": 1,
            //             "max_chars": 5,
            //             "password_combination": true,
            //             "password_repetation": false,
            //             "personal_information": false,
            //             "expairy_alert": false,
            //             "expairy_period": null,
            //             "alert_before": null,
            //             "login_attempts": false,
            //             "max_attempts": null,
            //             "next_attempt": null,
            //             "session_time_out": false,
            //             "time_out_value": null,
            //             "email": null,
            //             "reset_list": "mail"
            //         }
            //     ],
            //     "profile": "",
            //     "payroll_custom_configuration": [
            //         {
            //             "id": 2,
            //             "created_by": null,
            //             "created_date": "2024-10-19T07:10:32.750039",
            //             "modified_by": null,
            //             "modified_date": "2025-07-28T17:54:18.280570",
            //             "is_active": true,
            //             "is_draft": false,
            //             "notes": null,
            //             "code": "PCC001",
            //             "company_id": 2,
            //             "salary_deduction": "gross",
            //             "pf_limit": 7000,
            //             "cap_amount": 1800,
            //             "lop_deduction": "net_salary",
            //             "zero_payroll": true,
            //             "modify_issued_documents": false,
            //             "biometric_punch_only_inout": false,
            //             "grace_times": 0,
            //             "deduct_for_contract_emp": true,
            //             "is_deduction": true
            //         }
            //     ],
            //     "salary_calculation": "gross",
            //     "attendance_id": 18678,
            //     "working_hrs": null,
            //     "date": "2025-11-29",
            //     "check_in": null,
            //     "check_out": null,
            //     "pause": null,
            //     "resume": null,
            //     "shift_start_time": "2025-11-29T09:00:00",
            //     "shift_end_time": "2025-11-29T18:00:00",
            //     "clock_status": "resume"
            // }

            const shiftDetails = {
                attendance_id: response.data?.attendance_id,
                date: response.data?.date,
                check_in_time: response.data?.check_in,
                check_out_time: response.data?.check_out,
                shift_end_time: response.data?.shift_end_time,
                shift_start_time: response.data?.shift_start_time,
                working_hrs: response.data?.working_hrs,
                clock_status: response.data?.clock_status,
                pause: response.data?.pause,
                resume: response.data?.resume,
            }

            // Store some basic details in session storage
            if (custom_configuration) {
                sessionStorage.setItem("customConfig", JSON.stringify(custom_configuration[0]));
            }

            if (password_policy) {
                sessionStorage.setItem('password_policy', JSON.stringify(password_policy[0]))
            }

            if (shiftDetails) {
                sessionStorage.setItem("shiftDetails", JSON.stringify(shiftDetails));
                setAttendanceData(shiftDetails);
            }
            setAttendanceLoading(false);

            if (noticeBoardData) {
                // Map to initAnnouncements style
                const mappedAnnouncements = (Array.isArray(noticeBoardData) ? noticeBoardData : [noticeBoardData]).map(
                    (item, index) => ({
                        id: item.id || index + 1,
                        author: item.notice_by || "HR Team",
                        title: item.notice_title || "Notice",
                        content: item.description || "",
                        role: item.priority_level || "General",
                        posted: formatNoticeDate(item.published_date, item.published_time),
                        attachment: item.attachment || null, // Optional: keep attachment if you want
                    })
                );

                let meetings = [];
                let holidays = [];
                let onLeave = [];

                const birthdays = upcomingEvents.filter(
                    (e) => e.event === "birthday" && isToday(e.date)
                );

                const anniversaries = upcomingEvents.filter(
                    (e) => e.event === "anniversary" && isToday(e.date)
                );

                setMeetings(meetings);
                setHolidays(holidays);
                setAnniversaries(anniversaries);
                setBirthdays(birthdays);
                setOnLeave(onLeave);
                setAnnouncements(mappedAnnouncements);
            }
        } catch (error) {
            ToastMsg('error', error.message || "Failed to fetch data");
        }
    }, [fetch]);

    useEffect(() => {
        getData();
    }, []);

    const refreshAttendanceData = useCallback(async () => {
        await getData();
    }, [getData]);

    // user need to update their password so navigating to the security screen
    useEffect(() => {
        if (!is_password_updated) {
            navigate("/settings/security", { state: { fromEdit: true } });
        }
    }, [])

    const Placeholder = ({ icon: Icon, text }) => (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Icon className="w-10 h-10 mb-2" />
            <p className="text-sm">{text}</p>
        </div>
    );

    // Greeting function based on time
    const getGreeting = () => {
        const currentHour = new Date().getHours();

        if (currentHour >= 5 && currentHour < 12) {
            return "Good Morning";
        } else if (currentHour >= 12 && currentHour < 17) {
            return "Good Afternoon";
        } else if (currentHour >= 17 && currentHour < 21) {
            return "Good Evening";
        } else {
            return "Good Night";
        }
    };

    // Safe date formatting function
    const formatDate = (date) => {
        // Ensure date is a valid Date object
        const validDate = date instanceof Date ? date : new Date();

        // Check if date is valid
        if (isNaN(validDate.getTime())) {
            return "Invalid date";
        }

        return validDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <ContainerCard>
            <div className=" space-y-3 p-2 text-sm">
                {/* Greeting */}
                <div className="flex justify-between items-center">
                    <div className="text-left">
                        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                            {getGreeting()}, {user_name}!
                        </h1>
                        <p className="text-sm my-2 text-gray-500 dark:text-gray-400">
                            Here's your dashboard overview • {formatDate(today)}
                        </p>
                    </div>
                    <TimesheetManagement />
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* LEFT COLUMN */}
                    <div className="space-y-5 lg:col-span-1">
                        {/* Attendance */}
                        <AttendanceCard attendanceData={attendanceData} attendanceLoading={attendanceLoading} refreshData={refreshAttendanceData} />

                        {/* Meetings */}
                        <div className="bg-white min-h-48 dark:bg-gray-800 p-5 rounded-2xl shadow-md border hover:shadow-lg transition">
                            <h2 className="text-xs font-semibold mb-3 uppercase text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-purple-600" /> Meetings Today
                            </h2>
                            {meetings.length > 0 ? (
                                <ul className="space-y-2">
                                    {meetings.map((m) => (
                                        <li
                                            key={m.id}
                                            className="flex justify-between items-center border p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                                        >
                                            <div>
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                                                    {m.title}
                                                </p>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {m.time}
                                                </span>
                                            </div>
                                            <button className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md shadow">
                                                Join
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Placeholder icon={Calendar} text="No upcoming meetings" />
                            )}
                        </div>

                        {/* Holidays */}
                        <div className="bg-white min-h-48 dark:bg-gray-800 p-5 rounded-2xl shadow-md border hover:shadow-lg transition">
                            <h2 className="text-xs font-semibold mb-3 uppercase text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Gift className="w-4 h-4 text-pink-600" /> Upcoming Holidays
                            </h2>
                            {holidays.length > 0 ? (
                                <ul className="text-sm text-gray-800 dark:text-gray-100">
                                    {holidays.map((h) => (
                                        <li key={h.id} className="mb-1">
                                            <strong>{h.date}</strong> — {h.name}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Placeholder icon={Gift} text="No upcoming holidays" />
                            )}
                        </div>
                    </div>

                    {/* MIDDLE COLUMN */}
                    <div className="lg:col-span-2 space-y-5">
                        {/* Announcements */}
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-md border hover:shadow-lg transition">
                            <h2 className="text-xs font-semibold mb-4 uppercase tracking-wide text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Bell className="w-5 h-5 text-yellow-600" /> Announcements
                            </h2>

                            {announcements.length > 0 ? (
                                <div className="space-y-6">
                                    {announcements.map((a) => (
                                        <div
                                            key={a.id}
                                            className="border dark:border-gray-600 p-5 rounded-xl hover:shadow-md transition bg-gray-50 dark:bg-gray-700"
                                        >
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                                {a.author} • {a.posted}
                                            </p>

                                            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2 leading-snug">
                                                {a.title}
                                            </h3>

                                            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                                {a.content}
                                            </p>

                                            <div className=" flex justify-between items-center">
                                                {a.role && (
                                                    <span
                                                        className={`inline-block mt-3 px-2 py-0.5 text-xs font-semibold rounded-full
                                                        ${a.role.toLowerCase() === "high"
                                                                ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                                                                : a.role.toLowerCase() === "medium"
                                                                    ? "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
                                                                    : "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                                                            }`}
                                                    >
                                                        {a.role}
                                                    </span>
                                                )}


                                                {/* ✅ Attachment Section */}
                                                {a.attachment && (
                                                    <div className="mt-4">
                                                        {a.attachment.endsWith(".jpg") ||
                                                            a.attachment.endsWith(".jpeg") ||
                                                            a.attachment.endsWith(".png") ? (
                                                            <img
                                                                src={a.attachment}
                                                                alt="Notice Attachment"
                                                                className="rounded-lg max-h-48 object-cover border"
                                                            />
                                                        ) : (
                                                            <a
                                                                href={a.attachment}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs text-[#04b7b1] hover:underline underline-offset-2 flex items-center gap-1"
                                                            >
                                                                📎 View Attachment
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Placeholder icon={Bell} text="No announcements yet" />
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="lg:col-span-1 space-y-5">
                        {/* Birthdays */}
                        <div className="bg-white min-h-48 dark:bg-gray-800 p-5 rounded-2xl shadow-md border hover:shadow-lg transition">
                            <h2 className="text-xs font-semibold mb-3 uppercase text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Gift className="w-4 h-4 text-pink-500" /> Birthdays Today
                            </h2>
                            {birthdays.length > 0 ? (
                                <ul className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
                                    {birthdays.map((b) => (
                                        <li key={b.employee_id} className="flex justify-between">
                                            <span>{b.employee_name}</span>
                                            <span className="text-gray-500 dark:text-gray-400">{b.designation?.designation_name}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Placeholder icon={Gift} text="No birthdays today" />
                            )}
                        </div>

                        {/* Anniversaries */}
                        <div className="bg-white min-h-48 dark:bg-gray-800 p-5 rounded-2xl shadow-md border hover:shadow-lg transition">
                            <h2 className="text-xs font-semibold mb-3 uppercase text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-indigo-600" /> Work Anniversaries
                            </h2>
                            {anniversaries.length > 0 ? (
                                <ul className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
                                    {anniversaries.map((a) => {
                                        const years = calculateYears(a.date);

                                        return (
                                            <li key={a.employee_id} className="flex justify-between">
                                                <span>{a.employee_name}</span>
                                                <span className="text-gray-500 dark:text-gray-400">{years} yrs</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <Placeholder icon={Briefcase} text="No anniversaries today" />
                            )}
                        </div>

                        {/* Team on Leave */}
                        <div className="bg-white min-h-48 dark:bg-gray-800 p-5 rounded-2xl shadow-md border hover:shadow-lg transition">
                            <h2 className="text-xs font-semibold mb-3 uppercase text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Users className="w-4 h-4 text-red-600" /> Team on Leave
                            </h2>
                            {onLeave.length > 0 ? (
                                <ul className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
                                    {onLeave.map((p) => (
                                        <li key={p.id} className="flex justify-between">
                                            <span>{p.name}</span>
                                            <span className="text-gray-500 dark:text-gray-400">{p.role}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Placeholder icon={Users} text="No team members on leave" />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </ContainerCard>
    );
};

export default Home;